import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Infinity as InfinityIcon, Sparkles } from "lucide-react";
import { loadState } from "../lib/storage";

const QUANTUM_FACE =
  "https://static.prod-images.emergentagent.com/jobs/db8a3080-5e5f-4eab-addd-7a754d4ee19c/images/471b7347c49ab684fcc63c7c7e95423389b3feaf4e407b93085724ab173f3aef.jpeg";

const MULTIVERSE_LINES = [
  "Quantum physics suggests reality is stranger than it looks. Before it is measured, a particle can be described in a superposition of many possible states. In the many-worlds interpretation, each possible outcome branches into its own universe.",
  "Think of Doctor Strange stepping through the multiverse: endless mirrored realities folding into one another, each holding a different version of the same person. In one you became the sorcerer. In another you stayed the surgeon. In another you never walked through the door at all.",
  "We borrow that idea as inspiration, not as science. Your choices don't literally open doors to other universes, but imagining the you who took the leap, the you who stayed, the you who fell in love or built an empire can show you what matters most.",
  "Rabbit Hole Futures is your portal of imagination. Step through, meet the versions of you that could have been, and choose who you'll become in the one life you're living.",
];

const HOME_MESSAGE = [
  "What if every decision opened a different version of your life?",
  "What if you could meet the person you might have become?",
  "What would they tell you?",
  "Welcome to Rabbit Hole Futures.",
  "Your possibilities are waiting.",
];


const pathways = [
  { label: "Begin a New Journey", desc: "Craft your starting self and open three glowing doors.", icon: Compass, color: "cyan", testId: "pathway-begin" },
  { label: "Resume Your Rabbit Hole", desc: "Return to a saved journey already in motion.", icon: InfinityIcon, color: "purple", testId: "pathway-resume" },
  { label: "Explore in Guest Mode", desc: "Peek inside, no profile needed — your path saves locally.", icon: Sparkles, color: "gold", testId: "pathway-guest" },
];

const colorMap = {
  cyan: { line: "#00F0FF", glow: "rgba(0,240,255,0.6)" },
  purple: { line: "#B026FF", glow: "rgba(176,38,255,0.6)" },
  gold: { line: "#FFD700", glow: "rgba(255,215,0,0.55)" },
};

export default function Opening() {
  const [stage, setStage] = useState(0);
  const navigate = useNavigate();
  const saved = loadState();
  const hasResume = !!saved.journeyId;

  const handlePick = (idx) => {
    if (idx === 1 && hasResume) {
      navigate(`/journey/${saved.journeyId}`);
    } else {
      navigate("/create");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16 relative">
      <AnimatePresence mode="wait">
        {stage === 0 && (
          <motion.div
            key="s0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="flex flex-col items-center text-center max-w-2xl"
          >
            <div className="font-display text-sm md:text-base tracking-[0.4em] mb-2 text-white/80" data-testid="brand-name">RABBIT HOLE FUTURES</div>
            <div className="font-mono-label mb-10 text-amber-200/70" data-testid="brand-tagline">One Life. Infinite Possibilities. Meet the You That Could Have Been.</div>
            <motion.img
              src={QUANTUM_FACE}
              alt="A face made of quantum light"
              data-testid="quantum-face"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: [1, 1.03, 1] }}
              transition={{ opacity: { duration: 1.6 }, scale: { duration: 6, repeat: Infinity, ease: "easeInOut" } }}
              className="w-64 h-64 md:w-80 md:h-80 object-cover"
              style={{
                WebkitMaskImage: "radial-gradient(circle, #000 52%, transparent 72%)",
                maskImage: "radial-gradient(circle, #000 52%, transparent 72%)",
                filter: "drop-shadow(0 0 40px rgba(176,38,255,0.45))",
              }}
            />
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 1 }}
              className="mt-10 text-3xl md:text-5xl font-display font-light leading-[1.1] tracking-tight"
            >
              This is you.
              <br />
              <span className="text-glow-cyan">This is today.</span>
              <br />
              <span className="text-white/70">
                Everything after this
                <br />
                is a possibility.
              </span>
            </motion.h1>
            <div className="mt-10 space-y-2" data-testid="homepage-message">
              {HOME_MESSAGE.map((line, i) => (
                <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 + i * 0.35, duration: 0.8 }}
                  className={i === HOME_MESSAGE.length - 1 ? "font-display text-xl md:text-2xl text-glow-gold text-amber-100 pt-3" : "font-display text-lg md:text-xl font-light text-white/85"}>
                  {line}
                </motion.p>
              ))}
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.8, duration: 1 }} className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setStage(1)} data-testid="opening-continue-btn" className="btn-pill btn-pill-primary tracking-[0.18em] text-sm">
                ENTER THE RABBIT HOLE <ArrowRight size={16} />
              </button>
              <button onClick={() => (hasResume ? navigate(`/journey/${saved.journeyId}`) : navigate("/create"))} data-testid="explore-possibilities-btn" className="btn-pill btn-pill-gold tracking-[0.18em] text-sm">
                EXPLORE YOUR POSSIBILITIES
              </button>
            </motion.div>
            <div className="mt-14 space-y-5 text-left md:text-center" data-testid="multiverse-intro">
              {MULTIVERSE_LINES.map((line, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ delay: 4.4 + i * 0.9, duration: 1.1 }}
                  className={`text-sm md:text-base leading-relaxed ${i === MULTIVERSE_LINES.length - 1 ? "text-amber-200/90 italic" : "text-white/65"}`}
                >
                  {line}
                </motion.p>
              ))}
              <p className="text-[11px] text-white/35">Quantum ideas here are artistic inspiration. Every alternate life is an imaginative simulation, not a prediction.</p>
            </div>
          </motion.div>
        )}

        {stage === 1 && (
          <motion.div
            key="s1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full max-w-5xl"
          >
            <div className="text-center mb-10">
              <div className="font-mono-label mb-3 text-white/50">Choose your doorway</div>
              <h2 className="text-3xl md:text-4xl font-display font-light">
                Three pathways appear <span className="text-glow-purple">before you</span>
              </h2>
              <p className="mt-3 text-white/60 max-w-xl mx-auto">
                One life. Infinite possibilities. Pick a door — each one unfolds a different version of you.
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {pathways.map((p, idx) => {
                const c = colorMap[p.color];
                const Icon = p.icon;
                const disabled = idx === 1 && !hasResume;
                return (
                  <motion.button
                    key={p.label}
                    onClick={() => !disabled && handlePick(idx)}
                    disabled={disabled}
                    data-testid={p.testId}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: disabled ? 0.35 : 1, y: 0 }}
                    transition={{ delay: idx * 0.15, duration: 0.6 }}
                    whileHover={!disabled ? { y: -6 } : {}}
                    className="glass rounded-2xl p-7 text-left group relative overflow-hidden"
                    style={{
                      borderColor: c.line + "88",
                      boxShadow: `0 0 32px ${c.glow}`,
                      cursor: disabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-5"
                      style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${c.line}aa`, color: c.line }}
                    >
                      <Icon size={20} />
                    </div>
                    <h3 className="text-xl font-display font-semibold mb-2" style={{ color: c.line, textShadow: `0 0 14px ${c.glow}` }}>
                      {p.label}
                    </h3>
                    <p className="text-sm text-white/60 leading-relaxed">{p.desc}</p>
                    {disabled && (
                      <div className="font-mono-label mt-4" style={{ color: "rgba(255,255,255,0.3)" }}>
                        No saved journey yet
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
            <div className="mt-10 text-center text-xs text-white/35 font-mono-label">
              Simulated outcomes are possibilities, not predictions.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
